/**
 * Where to land after an OAuth round-trip.
 *
 * The provider comes back to /auth/callback, which knows nothing about the
 * page the sign-in started from — so that page leaves its path behind here
 * first, and the callback picks it up. Session storage, not a URL parameter,
 * because the state travelling to Google and back is the server's.
 *
 * Only a path on this site is ever honoured: anything absolute, or a
 * protocol-relative `//evil.example`, is an open redirect and is dropped.
 */

const KEY = 'postAuthRedirect';

/** True for a path this site can send someone to safely. */
export function isSafeRedirectPath(path: unknown): path is string {
  return typeof path === 'string' && /^\/(?!\/)/.test(path);
}

export function setPostAuthRedirect(path: string): void {
  try {
    if (isSafeRedirectPath(path)) window.sessionStorage.setItem(KEY, path);
  } catch {
    /* private browsing, or storage disabled — the default landing still works */
  }
}

/** The stored path, cleared as it is read so it is used once. */
export function takePostAuthRedirect(): string | null {
  try {
    const stored = window.sessionStorage.getItem(KEY);
    window.sessionStorage.removeItem(KEY);
    return isSafeRedirectPath(stored) ? stored : null;
  } catch {
    return null;
  }
}
